import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toMessage } from "@/api/errors";
import { createFolder, listFolders, updateFolder } from "@/api/lookups";
import type { Folder, FolderCreate } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DataTable } from "@/components/table/DataTable";
import { FolderDrawer } from "@/components/folder/FolderDrawer";
import { useAuthStore } from "@/stores/auth";

/**
 * Folders admin (papki-tenants v1).
 *
 * Admin-only: lists every folder, lets the admin create new ones, and
 * marks the default folder with a badge. Manager/user roles get a
 * forbidden placeholder — folder CRUD is strictly admin per the RBAC
 * matrix mirror (`folder_crud` is not yet a named action, but the SPEC
 * for this feature treats it as admin-only, mirroring `user_invite`).
 *
 * The route layer (`/admin/folders`) wraps this with a `RoleGuard`, so a
 * non-admin would normally never reach the page — the forbidden marker
 * is a defence-in-depth for callers that mount the component directly
 * (tests, embedded pages, etc.).
 */
export function FoldersAdmin() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.role);
  const isAdmin = role === "admin";
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  // Drawer-open state lives next to the page so the drawer can outlive
  // a click-through and the row click is a single source of truth.
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  // Папка, для которой админ просит выключить хранение видео. Пока она
  // здесь — открыт вопрос «точно выключить?»; PATCH уходит только после «да».
  const [videoOffFolder, setVideoOffFolder] = useState<Folder | null>(null);

  const folders = useQuery({
    queryKey: ["folders", "admin-list"],
    queryFn: () => listFolders(),
    enabled: isAdmin,
  });

  const storeVideo = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      updateFolder(id, { store_video: value }),
    onSuccess: async () => {
      setVideoOffFolder(null);
      await queryClient.invalidateQueries({ queryKey: ["folders"] });
    },
  });
  // `mutate`/`reset` стабильны между рендерами, а сам объект мутации — нет:
  // в зависимостях useCallback/useMemo нужны именно эти функции.
  const {
    mutate: mutateStoreVideo,
    reset: resetStoreVideo,
    isPending: storeVideoPending,
  } = storeVideo;

  const handleStoreVideoChange = useCallback(
    (folder: Folder, next: boolean) => {
      resetStoreVideo();
      // Включение безопасно — уходит сразу. Выключение удаляет видео у
      // будущих встреч, поэтому сначала спрашиваем подтверждение.
      if (next) {
        mutateStoreVideo({ id: folder.id, value: true });
        return;
      }
      setVideoOffFolder(folder);
    },
    [mutateStoreVideo, resetStoreVideo],
  );

  const columns: ColumnDef<Folder, unknown>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("folders.admin.columns.name"),
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-2">
              <span>{row.name}</span>
              {row.is_default ? (
                <span
                  className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                  data-testid={`default-folder-badge-${row.id}`}
                >
                  {t("folders.admin.defaultBadge")}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "store_video",
        header: t("folders.admin.columns.storeVideo"),
        cell: (info) => (
          <StoreVideoToggle
            folder={info.row.original}
            disabled={storeVideoPending}
            onChange={handleStoreVideoChange}
          />
        ),
      },
    ],
    [t, handleStoreVideoChange, storeVideoPending],
  );

  if (!isAdmin) {
    return (
      <div className="space-y-4" data-testid="forbidden">
        <h1 className="text-xl font-semibold">{t("folders.admin.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("folders.admin.forbiddenBody")}
        </p>
      </div>
    );
  }

  const openFolder: Folder | null =
    (folders.data ?? []).find((f) => f.id === openFolderId) ?? null;

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <h1 className="text-xl font-semibold">{t("folders.admin.title")}</h1>
        <Button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="open-create-folder"
        >
          {t("folders.admin.createButton")}
        </Button>
      </header>
      <DataTable
        data={folders.data ?? []}
        columns={columns}
        testId="folders-table"
        isLoading={folders.isLoading}
        onRowClick={(row) => setOpenFolderId(row.id)}
      />
      {storeVideo.isError && videoOffFolder === null ? (
        <p
          className="text-xs text-destructive"
          role="alert"
          data-testid="folder-store-video-error"
        >
          {toMessage(
            storeVideo.error,
            t("folders.admin.errors.storeVideoFailed"),
          )}
        </p>
      ) : null}
      <CreateFolderDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ["folders"] });
        }}
      />
      <FolderDrawer
        open={openFolder !== null}
        folder={openFolder}
        onClose={() => setOpenFolderId(null)}
      />
      <DisableStoreVideoDialog
        folder={videoOffFolder}
        pending={storeVideoPending}
        error={storeVideo.isError ? storeVideo.error : null}
        onClose={() => setVideoOffFolder(null)}
        onConfirm={() => {
          if (videoOffFolder === null) return;
          mutateStoreVideo({ id: videoOffFolder.id, value: false });
        }}
      />
    </div>
  );
}

interface StoreVideoToggleProps {
  folder: Folder;
  disabled: boolean;
  onChange: (folder: Folder, next: boolean) => void;
}

/** Переключатель «хранить видео» в строке папки. */
function StoreVideoToggle({
  folder,
  disabled,
  onChange,
}: StoreVideoToggleProps) {
  const { t } = useTranslation();
  return (
    // Строка таблицы кликабельна целиком и открывает ящик папки — клик по
    // переключателю не должен его открывать.
    <div onClick={(e) => e.stopPropagation()}>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={folder.store_video}
          disabled={disabled}
          onChange={(e) => onChange(folder, e.target.checked)}
          aria-label={t("folders.admin.storeVideo.toggleAria", {
            name: folder.name,
          })}
          data-testid={`folder-store-video-toggle-${folder.id}`}
        />
        <span className="text-muted-foreground">
          {folder.store_video
            ? t("folders.admin.storeVideo.on")
            : t("folders.admin.storeVideo.off")}
        </span>
      </label>
    </div>
  );
}

interface DisableStoreVideoDialogProps {
  /** Папка, у которой выключают видео; `null` — окно закрыто. */
  folder: Folder | null;
  pending: boolean;
  error: Error | null;
  onClose: () => void;
  onConfirm: () => void;
}

/** Подтверждение выключения: объясняет, что видео новых встреч удалят. */
function DisableStoreVideoDialog({
  folder,
  pending,
  error,
  onClose,
  onConfirm,
}: DisableStoreVideoDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={folder !== null}
      onOpenChange={(v) => (v ? undefined : onClose())}
    >
      <DialogContent data-testid="folder-store-video-confirm">
        <DialogHeader>
          <DialogTitle>
            {t("folders.admin.storeVideo.confirmTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <p>{t("folders.admin.storeVideo.confirmBody")}</p>
          <p className="text-muted-foreground">
            {t("folders.admin.storeVideo.confirmKeepsOld")}
          </p>
        </div>
        {error !== null ? (
          <p
            className="text-xs text-destructive"
            role="alert"
            data-testid="folder-store-video-error"
          >
            {toMessage(error, t("folders.admin.errors.storeVideoFailed"))}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={onConfirm}
            data-testid="confirm-disable-store-video"
          >
            {pending
              ? t("common.saving")
              : t("folders.admin.storeVideo.confirmSubmit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface CreateFolderDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface CreateFolderFormValues {
  name: string;
}

const EMPTY_FORM: CreateFolderFormValues = {
  name: "",
};

function CreateFolderDialog({
  open,
  onClose,
  onCreated,
}: CreateFolderDialogProps) {
  const { t } = useTranslation();
  const form = useForm<CreateFolderFormValues>({
    defaultValues: EMPTY_FORM,
  });

  const mutation = useMutation({
    mutationFn: (values: CreateFolderFormValues) => {
      const payload: FolderCreate = { name: values.name.trim() };
      return createFolder(payload);
    },
    onSuccess: () => {
      form.reset(EMPTY_FORM);
      onCreated();
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("folders.admin.createButton")}</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          data-testid="folder-create-form"
        >
          <div>
            <Label htmlFor="folder-name">
              {t("folders.admin.fields.name")}
            </Label>
            <Input
              id="folder-name"
              data-testid="folder-name-input"
              {...form.register("name", { required: true })}
            />
          </div>
          {mutation.isError ? (
            <p
              className="text-xs text-destructive"
              role="alert"
              data-testid="folder-create-error"
            >
              {toMessage(
                mutation.error,
                t("folders.admin.errors.createFailed"),
              )}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="submit-create-folder"
            >
              {mutation.isPending
                ? t("common.saving")
                : t("folders.admin.createButton")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
